import { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Dialog } from '@mui/material';
import CaseDetail from '../CaseDetail';
import Download from '../Download';
import { getCaseColumns, getCaseGridTemplate } from '../columns';
import './styles.scss';

const formatDate = (d) => (d ? moment(d).format('D MMM YYYY') : '-');

const trimId = (id) => (id.substring(0, 8));

const getStatusIcon = (status) => {
    if (status === 'Processing') {
        return <i className="material-icons status pending status-icon">timelapse</i>;
    }

    if (status === 'Submitted') {
        return <i className="material-icons status processing status-icon">timelapse</i>;
    }

    if (status === 'Accepted') {
        return <i className="material-icons status completed status-icon">check_circle_outline</i>;
    }

    // Otherwise, status === 'Processing Failed' or something not intended
    return <i className="material-icons status failed">warning</i>;
};

function Case({
    id,
    status,
    caseGroups,
    patientFirstName,
    patientLastName,
    doctorName,
    clinicName,
    labName,
    createdOn,
    receivedOn,
    dueOn,
    upperStlImageUrl,
    lowerStlImageUrl,
    fullStlImageUrl,
    upperPlyImageUrl,
    lowerPlyImageUrl,
    fullPlyImageUrl,
    orderIntegrationUploadZipUrl,
    prescriptionUrl,
    scanZipUrl,
    thumbnailUrl,
    template,
    instructions,
    labId,
    scans,
    numCases,
    index,
    open,
    switchOpen,
    caseDetailGlobalState,
    setCaseDetailGlobalState,
}) {
    const toggleHandler = (val) => {
        if (!open) switchOpen(index);
        else if (val === null) switchOpen(null);
        else switchOpen(val);
    };

    // Determine table columns
    const { scope } = localStorage;
    const scopeArray = (scope || '').split(' ');
    const hasAccess = (rule) => scopeArray.includes(rule);
    const canViewClinicsColumn = hasAccess('cases.display.clinics:read');
    const canViewLabsColumn = hasAccess('cases.display.labs:read');
    const columns = getCaseColumns(canViewClinicsColumn, canViewLabsColumn);
    const gridTemplateColumns = getCaseGridTemplate(columns);
    const canDownloadOrderIntegrationUploadZip = hasAccess('cases.orderintegrationuploadzip:download');
    const canDownloadPrescription = hasAccess('cases.prescriptions:download');
    const canUpdateCaseStatus = hasAccess('cases.status:write');
    const canDownloadSTL = hasAccess('cases.scans:download');
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const onCloseDownloadModal = () => setShowDownloadModal(false);
    const onOpenDownloadModal = () => setShowDownloadModal(true);

    const shortenId = (x) => x.substring(0, 8);
    const id8 = shortenId(id);

    const patientName = (`${patientFirstName || ''} ${patientLastName || ''}`).trim();
    const cells = {
        status: <span className="status-label">{status}</span>,
        patientName,
        doctorName,
        clinicName,
        labName,
        orderNumber: trimId(id),
        createdOn: formatDate(createdOn),
        receivedOn: formatDate(receivedOn),
        dueOn: formatDate(dueOn),
    };

    const isClickable = status !== null;
    const onOpenDownloadClick = (event) => {
        event.stopPropagation();
        onOpenDownloadModal();
    };
    const onRowClick = () => {
        if (!isClickable) {
            return;
        }

        toggleHandler();
    };
    const onRowKeyDown = (event) => {
        if (!isClickable || event.target !== event.currentTarget) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleHandler();
        }
    };

    return (
        <div className="case-row">
            <div
                style={{ gridTemplateColumns }}
                onClick={onRowClick}
                onKeyDown={onRowKeyDown}
                role="button"
                tabIndex={isClickable ? 0 : -1}
                aria-disabled={!isClickable}
                className={`cases-grid-row case-row-summary ${isClickable ? 'clickable' : ''}`}
            >
                {columns.map((column) => (
                    <div className={`data ${column.key === 'status' ? 'status-cell' : ''}`} key={column.key}>
                        {column.key === 'status' && (
                            <span className="status-icon-cell" aria-hidden="true">
                                {getStatusIcon(status)}
                            </span>
                        )}
                        {cells[column.key]}
                    </div>
                ))}
                <div className="data actions-cell">
                    <button type="button" className="downloads-button" onClick={onOpenDownloadClick} aria-label={`Open downloads for case ${id8}`}>
                        <i className="material-icons button" aria-hidden="true">more_horiz</i>
                    </button>
                </div>
            </div>
            {
                open && (
                    <CaseDetail
                        id={id}
                        groups={caseGroups}
                        upperStlImageUrl={upperStlImageUrl}
                        lowerStlImageUrl={lowerStlImageUrl}
                        fullStlImageUrl={fullStlImageUrl}
                        upperPlyImageUrl={upperPlyImageUrl}
                        lowerPlyImageUrl={lowerPlyImageUrl}
                        fullPlyImageUrl={fullPlyImageUrl}
                        orderIntegrationUploadZipUrl={orderIntegrationUploadZipUrl}
                        canDownloadOrderIntegrationUploadZip={canDownloadOrderIntegrationUploadZip}
                        canDownloadPrescription={canDownloadPrescription}
                        canUpdateCaseStatus={canUpdateCaseStatus}
                        canDownloadSTL={canDownloadSTL}
                        prescriptionUrl={prescriptionUrl}
                        thumbnailUrl={thumbnailUrl}
                        scanZipUrl={scanZipUrl}
                        template={template}
                        instructions={instructions}
                        patientName={patientName}
                        doctorName={doctorName}
                        labId={labId}
                        labName={labName}
                        clinicName={clinicName}
                        status={status}
                        createdOn={createdOn}
                        receivedOn={receivedOn}
                        dueOn={dueOn}
                        scans={scans}
                        numCases={numCases}
                        index={index}
                        setOpen={toggleHandler}
                        caseDetailGlobalState={caseDetailGlobalState}
                        setCaseDetailGlobalState={setCaseDetailGlobalState}
                    />
                )
            }
            <Dialog onClose={onCloseDownloadModal} open={showDownloadModal} style={{ zIndex: 9999 }}>
                <div className="downloads-popup">
                    <div className="title">
                        <h3>Downloads</h3>
                        <p>{`Case ID: ${id8}`}</p>
                    </div>
                    <div className="downloads">
                        <Download
                            id={id}
                            id8={id8}
                            orderIntegrationUploadZipUrl={orderIntegrationUploadZipUrl}
                            prescriptionUrl={prescriptionUrl}
                            scanZipUrl={scanZipUrl}
                            canDownloadOrderIntegrationUploadZip={canDownloadOrderIntegrationUploadZip}
                            canDownloadPrescription={canDownloadPrescription}
                            canUpdateCaseStatus={canUpdateCaseStatus}
                            canDownloadSTL={canDownloadSTL}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

Case.propTypes = {
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    caseGroups: PropTypes.arrayOf(
        PropTypes.object.isRequired,
    ),
    patientFirstName: PropTypes.string,
    patientLastName: PropTypes.string,
    doctorName: PropTypes.string,
    clinicName: PropTypes.string,
    labName: PropTypes.string,
    createdOn: PropTypes.string,
    receivedOn: PropTypes.string,
    dueOn: PropTypes.string,
    upperStlImageUrl: PropTypes.string,
    lowerStlImageUrl: PropTypes.string,
    fullStlImageUrl: PropTypes.string,
    upperPlyImageUrl: PropTypes.string,
    lowerPlyImageUrl: PropTypes.string,
    fullPlyImageUrl: PropTypes.string,
    orderIntegrationUploadZipUrl: PropTypes.string,
    prescriptionUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    scanZipUrl: PropTypes.string,
    template: PropTypes.string,
    instructions: PropTypes.string,
    labId: PropTypes.string,
    scans: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        dentalCaseId: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    numCases: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    open: PropTypes.bool.isRequired,
    switchOpen: PropTypes.func.isRequired,
    caseDetailGlobalState: PropTypes.shape({
        isRightPanelOpen: PropTypes.bool.isRequired,
        rightPanel: PropTypes.number.isRequired,
        showRenderTips: PropTypes.bool.isRequired,
    }).isRequired,
    setCaseDetailGlobalState: PropTypes.func.isRequired,
};

Case.defaultProps = {
    patientFirstName: '',
    patientLastName: '',
    doctorName: '',
    clinicName: '',
    labName: '',
    createdOn: '',
    receivedOn: '',
    dueOn: '',
    upperStlImageUrl: '',
    lowerStlImageUrl: '',
    fullStlImageUrl: '',
    upperPlyImageUrl: '',
    lowerPlyImageUrl: '',
    fullPlyImageUrl: '',
    orderIntegrationUploadZipUrl: '',
    prescriptionUrl: '',
    thumbnailUrl: '',
    scanZipUrl: '',
    template: '',
    instructions: '',
    labId: '',
    caseGroups: [],
};

export default Case;
